import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { CommunicationLogBundleDoc, CommunicationRecord, LabelId, LogMetaItem } from '#types';
import { COMMUNICATION_LOG_BUNDLE_SIZE } from '#types/System.Parameters.types';
import { firestore } from '../../../../../services/startFirestore';

type CommunicationLogParent =
    | { parentDocPath: string }
    | { formId: string; formVersionId: string }
    | { entityId: string; entityRecordId: string };

type AppendRecordParams = CommunicationLogParent & {
    fieldId: string;
    record: CommunicationRecord;
};

type ReplaceRecordParams = CommunicationLogParent & {
    bundleDocId: string;
    record: CommunicationRecord;
};

type SetRecordLabelsParams = CommunicationLogParent & {
    bundleDocId: string;
    recordId: string;
    labelIds: LabelId[];
};

type RemoveRecordParams = CommunicationLogParent & {
    fieldId: string;
    recordId: string;
    bundleDocId?: string;
};

const resolveParentDocPath = (parent: CommunicationLogParent): string => {
    if ('parentDocPath' in parent) {
        return parent.parentDocPath;
    }
    if ('entityId' in parent) {
        return `entities/${parent.entityId}/records/${parent.entityRecordId}`;
    }
    return `forms/${parent.formId}/formVersions/${parent.formVersionId}`;
};

export const CommunicationLogBundles = {
    appendRecord: async (params: AppendRecordParams): Promise<void> => {
        const { fieldId, record } = params;
        const parentDocPath = resolveParentDocPath(params);
        const bundlesPath = `${parentDocPath}/communicationLogBundles`;

        const parentDocRef = doc(firestore, parentDocPath);
        const bundlesCollectionRef = collection(firestore, bundlesPath);

        await runTransaction(firestore, async (transaction) => {
            const parentDocSnap = await transaction.get(parentDocRef);
            
            if (!parentDocSnap.exists()) {
                throw new Error(`Parent document not found: ${parentDocPath}`);
            }

            const parentDocData = parentDocSnap.data();
            const meta: LogMetaItem | undefined = 
                parentDocData?.logMeta?.communicationLog?.[fieldId];

            const currentLogDocId = meta?.currentLogDocId;
            const currentLogEntryCount = meta?.currentLogEntryCount ?? 0;

            // If we have a current bundle with room, append to it
            if (currentLogDocId && currentLogEntryCount < COMMUNICATION_LOG_BUNDLE_SIZE) {
                const currentBundleRef = doc(firestore, bundlesPath, currentLogDocId);
                const currentBundleSnap = await transaction.get(currentBundleRef);
                
                const currentEntries = currentBundleSnap.exists()
                    ? ((currentBundleSnap.data() as Partial<CommunicationLogBundleDoc>).entries ?? [])
                    : [];

                const nextEntries = [record, ...currentEntries];

                transaction.update(currentBundleRef, {
                    entries: nextEntries,
                    entryCount: nextEntries.length,
                    updatedAt: serverTimestamp(),
                });

                transaction.update(parentDocRef, {
                    [`logMeta.communicationLog.${fieldId}.currentLogEntryCount`]: nextEntries.length,
                });

                return;
            }

            // Create a new bundle
            const newBundleRef = doc(bundlesCollectionRef);
            const bundleDoc: CommunicationLogBundleDoc = {
                docType: 'communicationLogBundle',
                fieldId,
                entries: [record],
                entryCount: 1,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            transaction.set(newBundleRef, bundleDoc);

            const newMeta: LogMetaItem = {
                currentLogDocId: newBundleRef.id,
                currentLogEntryCount: 1,
            };

            transaction.update(parentDocRef, {
                [`logMeta.communicationLog.${fieldId}`]: newMeta,
            });
        });
    },

    /**
     * Rewrites one entry of a bundle in place, matched by id, with the record
     * the caller authored — the email and phone editors, which own every field
     * they send. The stored `deleted` state is carried through rather than taken
     * from the incoming record: a caller edits the row it was handed, and the log
     * hands it only live rows, so trusting the incoming value would resurrect any
     * entry a second session deleted between the read and the write.
     *
     * A caller changing one field of a row it did not author wants
     * `setRecordLabels`, not this: a whole-record write reverts anything the
     * backend settled on that row after the caller read it.
     */
    replaceRecord: async (params: ReplaceRecordParams): Promise<void> => {
        const { bundleDocId, record } = params;
        const parentDocPath = resolveParentDocPath(params);
        const bundlesPath = `${parentDocPath}/communicationLogBundles`;
        const bundleRef = doc(firestore, bundlesPath, bundleDocId);

        await runTransaction(firestore, async (transaction) => {
            const bundleSnap = await transaction.get(bundleRef);

            if (!bundleSnap.exists()) {
                throw new Error(`Communication log bundle not found: ${bundleDocId}`);
            }

            const currentEntries = ((bundleSnap.data() as Partial<CommunicationLogBundleDoc>).entries ?? []) as CommunicationRecord[];
            const nextEntries = currentEntries.map((entry) =>
                entry?.id === record.id ? { ...record, deleted: entry.deleted === true } : entry
            );

            transaction.update(bundleRef, {
                entries: nextEntries,
                updatedAt: serverTimestamp(),
            });
        });
    },

    /**
     * Sets the label assignment on one entry and nothing else, matched by id.
     * The stored entry is the base, so a settlement the backend wrote in place
     * between the row's read and this commit — an API entry moving from
     * `pending` to `success` with its order number and response payload — is
     * still there afterwards.
     *
     * Throws rather than writing when the entry has left the bundle, so a
     * vanished row reports instead of committing a no-op the user reads as a
     * saved label.
     */
    setRecordLabels: async (params: SetRecordLabelsParams): Promise<void> => {
        const { bundleDocId, recordId, labelIds } = params;
        const parentDocPath = resolveParentDocPath(params);
        const bundleRef = doc(firestore, `${parentDocPath}/communicationLogBundles`, bundleDocId);

        await runTransaction(firestore, async (transaction) => {
            const bundleSnap = await transaction.get(bundleRef);

            if (!bundleSnap.exists()) {
                throw new Error(`Communication log bundle not found: ${bundleDocId}`);
            }

            const currentEntries = ((bundleSnap.data() as Partial<CommunicationLogBundleDoc>).entries ?? []) as CommunicationRecord[];

            if (!currentEntries.some((entry) => entry?.id === recordId)) {
                throw new Error(`Communication log record ${recordId} not found in bundle ${bundleDocId}`);
            }

            transaction.update(bundleRef, {
                entries: currentEntries.map((entry) =>
                    entry?.id === recordId ? { ...entry, labelIds } : entry
                ),
                updatedAt: serverTimestamp(),
            });
        });
    },

    removeRecord: async (params: RemoveRecordParams): Promise<void> => {
        const { fieldId, recordId, bundleDocId } = params;
        if (!bundleDocId) {
            throw new Error('bundleDocId is required to remove a record');
        }

        const parentDocPath = resolveParentDocPath(params);
        const bundlesPath = `${parentDocPath}/communicationLogBundles`;

        const parentDocRef = doc(firestore, parentDocPath);
        const bundleRef = doc(firestore, bundlesPath, bundleDocId);

        await runTransaction(firestore, async (transaction) => {
            const [parentDocSnap, bundleSnap] = await Promise.all([
                transaction.get(parentDocRef),
                transaction.get(bundleRef),
            ]);

            if (!parentDocSnap.exists()) {
                throw new Error(`Parent document not found: ${parentDocPath}`);
            }

            if (!bundleSnap.exists()) {
                throw new Error(`Communication log bundle not found: ${bundleDocId}`);
            }

            const currentEntries = ((bundleSnap.data() as Partial<CommunicationLogBundleDoc>).entries ?? []) as CommunicationRecord[];
            
            // Mark the record as deleted instead of removing it
            const nextEntries = currentEntries.map((entry) => 
                entry?.id === recordId ? { ...entry, deleted: true } : entry
            );

            transaction.update(bundleRef, {
                entries: nextEntries,
                updatedAt: serverTimestamp(),
            });

            // Update meta count if this is the current bundle
            const parentDocData = parentDocSnap.data();
            const meta: LogMetaItem | undefined = 
                parentDocData?.logMeta?.communicationLog?.[fieldId];

            if (meta?.currentLogDocId === bundleDocId) {
                transaction.update(parentDocRef, {
                    [`logMeta.communicationLog.${fieldId}.currentLogEntryCount`]: nextEntries.length,
                });
            }
        });
    },
};
